import React, { useState } from 'react';
import { Search, Paperclip, Send } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import { Card, Flex } from '@/components/ui/Layout';
import styles from '@/components/ui/Table/Table.module.css';

interface Chat {
  id: string;
  name: string;
  username?: string;
  lastMessage: string;
  timestamp: string;
  unread?: number;
  avatar: string;
  isOnline: boolean;
}

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isSent: boolean;
}

const TelegramSection: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const { isMobile } = useResponsive();
  const [showChatList, setShowChatList] = useState(true);

  const chats: Chat[] = [
    {
      id: '1',
      name: 'Алия Сатпаева',
      username: '@aliya_s',
      lastMessage: 'Добрый день! Хотела уточнить...',
      timestamp: '14:30',
      unread: 1,
      avatar: '/api/placeholder/40/40',
      isOnline: true
    },
    {
      id: '2',
      name: 'Марат Алиев',
      username: '@marat_a',
      lastMessage: 'Заказ получил, спасибо!',
      timestamp: '13:45',
      avatar: '/api/placeholder/40/40',
      isOnline: false
    }
  ];

  const messages: Message[] = [
    {
      id: '1',
      text: 'Здравствуйте, хотел уточнить по поводу доставки.',
      timestamp: '14:25',
      isSent: false
    },
    {
      id: '2',
      text: 'Здравствуйте! Да, конечно. Доставка осуществляется ежедневно с 10:00 до 22:00.',
      timestamp: '14:30',
      isSent: true
    }
  ];

  const handleSendMessage = () => {
    if (messageText.trim()) {
      // Здесь будет логика отправки сообщения
      setMessageText('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && messageText.trim()) {
      handleSendMessage();
    }
  };

  const handleChatSelect = (chatId: string) => {
    setSelectedChat(chatId);
    // На мобильных устройствах скрываем список чатов при выборе чата
    if (isMobile) {
      setShowChatList(false);
    }
  };

  const handleBackToList = () => {
    if (isMobile) {
      setShowChatList(true);
    }
  };

  return (
    <Card className="h-[calc(100vh-180px)] overflow-hidden" padding="none" shadow="sm">
      {/* Статистика */}
      <div className="w-full bg-white border-b border-[#A7ABAA]/20">
        <Flex 
          direction={isMobile ? "column" : "row"}
          justify="between"
          align={isMobile ? "start" : "center"}
          className="px-3 sm:px-6 py-3 sm:py-4"
        >
          <Flex align="center" gap="xs">
            <div className="w-2 h-2 bg-[#245D33] rounded-full"></div>
            <span className="text-sm font-medium text-[#465357]">Telegram подключен</span>
          </Flex>
          <Flex 
            justify={isMobile ? "between" : "start"} 
            gap={isMobile ? "none" : "lg"}
            className={isMobile ? "mt-2 w-full" : ""}
          >
            <div>
              <div className="text-xs sm:text-sm text-[#A7ABAA]">Конверсия</div>
              <div className="text-base sm:text-lg font-semibold text-[#465357]">32%</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-[#A7ABAA]">Время ответа</div>
              <div className="text-base sm:text-lg font-semibold text-[#465357]">1.8 мин</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-[#A7ABAA]">Активные чаты</div>
              <div className="text-base sm:text-lg font-semibold text-[#465357]">18</div>
            </div>
          </Flex>
        </Flex>
      </div>

      <Flex className="flex-1">
        {/* Список чатов - показываем только если не выбран чат на мобильных или всегда на десктопе */}
        {(showChatList || !isMobile) && (
          <div className={`${isMobile ? "w-full" : "w-80"} border-r border-[#A7ABAA]/20 flex flex-col`}>
            <div className="p-4 border-b border-[#A7ABAA]/20">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Поиск в Telegram..."
                  className="w-full pl-10 pr-4 py-2 bg-[#EFF6EF] border border-[#A7ABAA]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#245D33]"
                />
                <Search className="w-5 h-5 text-[#A7ABAA] absolute left-3 top-2.5" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => handleChatSelect(chat.id)}
                  className={`p-4 border-b border-[#A7ABAA]/20 cursor-pointer hover:bg-[#EFF6EF] ${
                    selectedChat === chat.id ? 'bg-[#EFF6EF]' : ''
                  }`}
                >
                  <Flex gap="sm">
                    <div className="relative">
                      <img
                        src={chat.avatar}
                        alt={chat.name}
                        className="w-10 h-10 rounded-full"
                      />
                      {chat.isOnline && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#245D33] border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Flex justify="between" align="baseline">
                        <div>
                          <h3 className="text-sm font-medium text-[#465357]">{chat.name}</h3>
                          <p className="text-xs text-[#A7ABAA]">{chat.username}</p>
                        </div>
                        <span className="text-xs text-[#A7ABAA]">{chat.timestamp}</span>
                      </Flex>
                      <p className="text-sm text-[#A7ABAA] truncate mt-1">{chat.lastMessage}</p>
                    </div>
                    {chat.unread && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#245D33] text-white text-xs font-medium">
                          {chat.unread}
                        </span>
                      </div>
                    )}
                  </Flex>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Область чата - показываем только если выбран чат на мобильных или всегда на десктопе */}
        {(!showChatList || !isMobile) && selectedChat ? (
          <div className={`${isMobile ? "w-full" : "flex-1"} flex flex-col`}>
            {/* Заголовок чата */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#A7ABAA]/20">
              <Flex justify="between" align="center">
                <Flex align="center" gap="sm">
                  {isMobile && (
                    <button 
                      onClick={handleBackToList}
                      className="mr-2 text-[#465357]"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                      </svg>
                    </button>
                  )}
                  <img
                    src={chats.find(chat => chat.id === selectedChat)?.avatar}
                    alt=""
                    className="w-8 h-8 rounded-full"
                  />
                  <div>
                    <div className="font-medium text-[#465357]">
                      {chats.find(chat => chat.id === selectedChat)?.name}
                    </div>
                    <div className="text-xs text-[#A7ABAA]">
                      {chats.find(chat => chat.id === selectedChat)?.username}
                    </div>
                  </div>
                </Flex>
              </Flex>
            </div>

            {/* Сообщения */}
            <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
              <Flex direction="column" gap="md">
                {messages.map((message) => (
                  <Flex key={message.id} justify={message.isSent ? 'end' : 'start'}>
                    {!message.isSent && (
                      <img
                        src={chats.find(chat => chat.id === selectedChat)?.avatar}
                        alt=""
                        className="w-8 h-8 rounded-full mr-2"
                      />
                    )}
                    <div className={`max-w-[80%] sm:max-w-[70%] rounded-lg py-2 px-4 ${
                      message.isSent 
                        ? 'bg-[#245D33] text-white' 
                        : 'bg-[#EFF6EF] text-[#465357]'
                    }`}>
                      <p className="text-sm">{message.text}</p>
                      <span className={`text-xs ${message.isSent ? 'text-white/80' : 'text-[#A7ABAA]'} mt-1`}>
                        {message.timestamp}
                      </span>
                    </div>
                  </Flex>
                ))}
              </Flex>
            </div>

            {/* Ввод сообщения */}
            <div className="p-4 border-t border-[#A7ABAA]/20">
              <Flex align="center" gap="xs">
                <button className="p-2 hover:bg-[#EFF6EF] rounded-full hidden sm:block">
                  <Paperclip className="w-5 h-5 text-[#A7ABAA]" />
                </button>
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Написать сообщение..."
                  className="flex-1 bg-[#EFF6EF] border border-[#A7ABAA]/20 rounded-lg px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#245D33]"
                />
                {messageText.trim() ? (
                  <button 
                    onClick={handleSendMessage}
                    className="p-2 bg-[#245D33] text-white rounded-full hover:bg-[#245D33]/90"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                ) : (
                  <button className="p-2 hover:bg-[#EFF6EF] rounded-full">
                    <Send className="w-5 h-5 text-[#A7ABAA]" />
                  </button>
                )}
              </Flex>
            </div>
          </div>
        ) : (!showChatList && !selectedChat) || (!showChatList && !isMobile && !selectedChat) ? (
          <div className={styles.emptyContainer}>
            Выберите чат для просмотра
          </div>
        ) : null}
      </Flex>
    </Card>
  );
};

export default TelegramSection;